import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { adAudienceEligible, nativeAdsIncluded } from "./ad-audience";
import { acquireFullscreen, automaticAdAllowed, recordAdBoot, recordFullscreenShown } from "./fullscreen-policy";
interface OpeningPlugin {
  prepare(options: { adId: string; adult: boolean; npa: boolean }): Promise<void>;
  show(options: { adult: boolean }): Promise<{ shown: boolean }>;
  cancel(): Promise<void>;
  addListener(event: "appOpenShowed", callback: () => void): Promise<PluginListenerHandle>;
}
const opening = registerPlugin<OpeningPlugin>("VarnaxAppOpen");
/** Race ad preparation against REAL boot work. Never wait for a late load, never show over a usable menu. */
export function withOpeningAd<T>(loading: Promise<T>): Promise<T> {
  if (!nativeAdsIncluded() || !Capacitor.isPluginAvailable("VarnaxAppOpen")) return loading;
  recordAdBoot();
  if (!adAudienceEligible() || !automaticAdAllowed("app-open")) return loading;
  let stillLoading = true, started = false, preparing = false;
  const task = (async () => {
    let release: (() => void) | null = null, listener: PluginListenerHandle | undefined;
    try {
      // Keep the loading shell small; consent UI and ad configuration are not cold-start dependencies.
      const { adMobConsentStillAllowed, findAdvertisingPlacement, initializeAdMobForRewards, loadAdvertisingConfig, recordPlacementExposure } = await import("./advertising");
      if (!stillLoading || !adAudienceEligible()) return;
      const config = await loadAdvertisingConfig();
      const placement = findAdvertisingPlacement(config, "APP_OPEN", "ANDROID", "APP_OPEN");
      if (!stillLoading || !placement || !await initializeAdMobForRewards(config) || !stillLoading) return;
      preparing = true;
      await opening.prepare({ adId: (config.providers.admob.testMode ?? config.testMode) ? "ca-app-pub-3940256099942544/9257395921" : placement.unitId,
        adult: true, npa: config.consent.mode === "NON_PERSONALIZED" });
      if (!stillLoading || !adAudienceEligible() || !adMobConsentStillAllowed() || document.visibilityState === "hidden" || !automaticAdAllowed("app-open")) return;
      release = acquireFullscreen("app-open"); if (!release) return;
      listener = await opening.addListener("appOpenShowed", () => { recordFullscreenShown("app-open"); recordPlacementExposure(placement); });
      if (!stillLoading || !adAudienceEligible() || !adMobConsentStillAllowed()) return;
      started = true;
      await opening.show({ adult: true });
    } catch { /* Unavailable/declined ads must not block startup. */ }
    finally {
      await listener?.remove().catch(() => undefined);
      if (preparing) await opening.cancel().catch(() => undefined);
      release?.();
    }
  })();
  return loading.then(async value => {
    stillLoading = false;
    if (started) await task;
    else if (preparing) void opening.cancel().catch(() => undefined);
    return value;
  }, error => {
    stillLoading = false;
    if (preparing) void opening.cancel().catch(() => undefined);
    throw error;
  });
}
